import { createGamingClient } from './gaming/client';
import { createStudioApplication } from './application';
import type { StudioApi } from './engine/api';
import { createSwarmTestHook, type SwarmTestHook } from './engine/swarmTestHook';
import { takeStudioFragment } from './net/connect-link';
import { mountStudioUi } from './ui';
import { applyDocumentTheme, resolveTheme, systemPrefersLight } from './ui/theme/theme';
import { loadPrefs } from './ui/uiStore';

declare global {
  interface Window {
    /** Handle for diagnostics and browser tests. */
    __studio?: StudioApi;
    /** Swarm-mode hook for the e2e suites; development builds only. */
    __OMP_STUDIO__?: SwarmTestHook;
  }
}

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');

// Read and strip `#connect=` (and `#theme=`, `#embed=`, `#dock=`) before anything else: the stream key it carries
// must leave the address bar at once.
const { connect, theme, embedded, dock } = takeStudioFragment(window.location, window.history);

// Set the theme before React mounts so the first paint already has the right colours.
let storage: Storage | null = null;
try {
  storage = window.localStorage;
} catch {
  // Blocked storage: the default preference applies.
}
applyDocumentTheme(resolveTheme({ hash: theme, parent: null, preference: loadPrefs(storage).theme, systemLight: systemPrefersLight() }));

const gamingProvider = embedded && window.parent !== window && connect.link?.portalUrl && import.meta.env.VITE_GAMING_STUDIO_ENABLED === 'true'
  ? createGamingClient({ parentOrigin: connect.link.portalUrl }) : undefined;
const studio = createStudioApplication({ embedded, gamingProvider });
// Development only: both hooks can drive the studio, and the swarm one can redirect the stream key.
// The e2e suites run the studio under Vite dev, where DEV is true; production builds drop both.
if (import.meta.env.DEV) {
  window.__studio = studio;
  window.__OMP_STUDIO__ = createSwarmTestHook(studio);
}

mountStudioUi(container, studio, { connect, theme, embedded, dock });
// Failures are surfaced through runtime.fatalError, which the UI renders.
studio.init().catch(() => undefined);
